import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Tag,
  Table,
  Input,
  Pagination,
  Select,
  DatePicker,
  Switch,
  Space,
  Tooltip,
  Dropdown,
  Menu,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  BarChartOutlined,
  MoreOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  GiftOutlined,
  ArrowLeftOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import {
  getAllPromotionsPaginated,
  deletePromotionById,
  togglePromotionStatus,
  duplicatePromotion,
} from "@api/subscription/promotion";
import { SuccessNotification, ErrorNotification } from "@components";
import { format } from "date-fns";

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const promotionTypes = [
  { value: "banner", label: "Banner" },
  { value: "popup", label: "Popup" },
  { value: "floating", label: "Floating" },
  { value: "inline", label: "Inline" },
  { value: "exit_intent", label: "Exit Intent" },
  { value: "gamified", label: "Gamified" },
];

const PromotionsList = ({ history }) => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    status: undefined,
    type: undefined,
    dateRange: undefined,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchPromotions();
  }, [pagination.current, pagination.pageSize, searchText, filters]);

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const res = await getAllPromotionsPaginated({
        page: pagination.current,
        perPage: pagination.pageSize,
        searchText,
        status: filters.status,
        type: filters.type,
        startDate: filters.dateRange?.[0]?.toISOString(),
        endDate: filters.dateRange?.[1]?.toISOString(),
      });
      setPromotions(res.data.promotions || []);
      setPagination((p) => ({
        ...p,
        total: res.data.paginationData?.totalPromotions || 0,
      }));
    } catch (err) {
      console.error("Fetch promotions error", err);
      ErrorNotification("Failed to fetch promotions");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    history.push("/super-admin/promotions/create");
  };

  const handleEdit = (id) => {
    history.push(`/super-admin/promotions/update/${id}`);
  };

  const handleViewAnalytics = (id) => {
    history.push(`/super-admin/promotions/${id}/analytics`);
  };

  const handleDelete = (id, name) => {
    Modal.confirm({
      title: "Delete Promotion",
      content: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deletePromotionById(id);
          SuccessNotification("Promotion deleted successfully");
          fetchPromotions();
        } catch (error) {
          ErrorNotification("Error deleting promotion");
        }
      },
    });
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await togglePromotionStatus(id);
      SuccessNotification(
        `Promotion ${currentStatus ? "deactivated" : "activated"} successfully`
      );
      fetchPromotions();
    } catch (error) {
      ErrorNotification("Error updating promotion status");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicatePromotion(id);
      SuccessNotification("Promotion duplicated successfully");
      fetchPromotions();
    } catch (error) {
      ErrorNotification("Error duplicating promotion");
    }
  };

  const clearFilters = () => {
    setFilters({
      status: undefined,
      type: undefined,
      dateRange: undefined,
    });
    setSearchText("");
  };

  const getTypeColor = (type) => {
    const colors = {
      banner: "blue",
      popup: "purple",
      floating: "cyan",
      inline: "green",
      exit_intent: "orange",
      gamified: "magenta",
    };
    return colors[type] || "default";
  };

  const getStatusTag = (promotion) => {
    const now = new Date();
    const startDate = promotion.schedule?.startDate
      ? new Date(promotion.schedule.startDate)
      : null;
    const endDate = promotion.schedule?.endDate
      ? new Date(promotion.schedule.endDate)
      : null;

    if (!promotion.isActive) {
      return <Tag color="default">Inactive</Tag>;
    }

    if (startDate && now < startDate) {
      return <Tag color="warning">Scheduled</Tag>;
    }

    if (endDate && now > endDate) {
      return <Tag color="error">Expired</Tag>;
    }

    return <Tag color="success">Active</Tag>;
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.slug && (
            <div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                /{record.slug}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag color={getTypeColor(type)}>
          {type?.replace("_", " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => getStatusTag(record),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleStatus(record._id, isActive)}
          size="small"
        />
      ),
    },
    {
      title: "Dates",
      key: "dates",
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>
          {record.schedule?.startDate && (
            <div>
              Start: {format(new Date(record.schedule.startDate), "MMM d, yyyy")}
            </div>
          )}
          {record.schedule?.endDate && (
            <div>
              End: {format(new Date(record.schedule.endDate), "MMM d, yyyy")}
            </div>
          )}
          {!record.schedule?.startDate && !record.schedule?.endDate && (
            <Text type="secondary">No dates set</Text>
          )}
        </div>
      ),
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => {
        if (!record.discount) return <Text type="secondary">-</Text>;
        const { discountType, discountValue, currency } = record.discount;
        if (discountType === "percentage") {
          return <Tag color="green">{discountValue}% OFF</Tag>;
        }
        return (
          <Tag color="green">
            {currency || "$"}
            {discountValue} OFF
          </Tag>
        );
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (priority) => (
        <Tag color={priority >= 8 ? "red" : priority >= 5 ? "orange" : "blue"}>
          {priority || 0}
        </Tag>
      ),
    },
    {
      title: "Views",
      key: "views",
      render: (_, record) => (
        <Tooltip title="Total views">
          <Space>
            <EyeOutlined />
            {record.stats?.totalViews?.toLocaleString() || 0}
          </Space>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEdit(record._id)}>
              Edit
            </Menu.Item>
            <Menu.Item key="analytics" icon={<BarChartOutlined />} onClick={() => handleViewAnalytics(record._id)}>
              Analytics
            </Menu.Item>
            <Menu.Item key="duplicate" icon={<CopyOutlined />} onClick={() => handleDuplicate(record._id)}>
              Duplicate
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="delete" icon={<DeleteOutlined />} danger onClick={() => handleDelete(record._id, record.name)}>
              Delete
            </Menu.Item>
          </Menu>
        );

        return (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record._id)}
              title="Edit"
            />
            <Button
              type="text"
              size="small"
              icon={<BarChartOutlined />}
              onClick={() => handleViewAnalytics(record._id)}
              title="Analytics"
            />
            <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
              <Button type="text" size="small" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <Card style={{ borderRadius: "0", minHeight: "100vh" }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space align="center">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push("/super-admin/promotions/analytics")}
              style={{ padding: "4px 8px" }}
            />
            <GiftOutlined style={{ fontSize: 24, color: "#8B5CF6" }} />
            <Title level={3} style={{ margin: 0 }}>
              Promotions
            </Title>
          </Space>
          <Text type="secondary" style={{ display: "block", marginTop: 4, marginLeft: 40 }}>
            Manage promotional banners, popups, and special offers
          </Text>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<LineChartOutlined />}
              onClick={() => history.push("/super-admin/promotions/analytics")}
            >
              Analytics
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Create Promotion
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Filters */}
      <Card
        size="small"
        style={{ marginBottom: 16, background: "#fafafa" }}
        bodyStyle={{ padding: "12px 16px" }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Search by name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={fetchPromotions}
              allowClear
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Status"
              style={{ width: "100%" }}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              allowClear
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="scheduled">Scheduled</Option>
              <Option value="expired">Expired</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Type"
              style={{ width: "100%" }}
              value={filters.type}
              onChange={(value) => setFilters({ ...filters, type: value })}
              allowClear
            >
              {promotionTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: "100%" }}
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            />
          </Col>
          <Col>
            <Space>
              <Tooltip title="Clear Filters">
                <Button icon={<FilterOutlined />} onClick={clearFilters}>
                  Clear
                </Button>
              </Tooltip>
              <Tooltip title="Refresh">
                <Button
                  icon={<ReloadOutlined spin={loading} />}
                  onClick={fetchPromotions}
                />
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={promotions}
        pagination={false}
        loading={loading}
        rowKey={(record) => record._id}
        scroll={{ x: "max-content" }}
      />

      {/* Pagination */}
      <Row justify="end" style={{ marginTop: 16 }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger
          showTotal={(total) => `Total ${total} promotions`}
          onChange={(page, pageSize) =>
            setPagination({ ...pagination, current: page, pageSize })
          }
        />
      </Row>
    </Card>
  );
};

export default PromotionsList;
